import {
    ArrowRightStartOnRectangleIcon,
    ChevronUpIcon,
    LightBulbIcon,
    ShieldCheckIcon,
    UserCircleIcon,
} from "@heroicons/react/16/solid";
import {
    BuildingOfficeIcon,
    ChartBarIcon,
    ClipboardDocumentCheckIcon,
    Cog8ToothIcon,
    FolderIcon,
    QuestionMarkCircleIcon,
    UserGroupIcon,
    UserIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { ActionType } from "@sp-crm/core";
import { Avatar } from "components/layout/avatar";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from "components/layout/dropdown";
import {
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarItem,
    SidebarLabel,
    SidebarSection,
    SidebarSpacer,
} from "components/layout/sidebar";
import { getInitials } from "components/ui/persona";
import React from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
    useCurrentTenant,
    useCurrentUser,
    useFeature,
    useIsAllowed,
} from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { ApplicationState } from "store/state";
import { isAllowed } from "../../util/permissions";
import { WhiteLabelRestricted } from "./white-label-restricted";

const NgAppLogo: React.FC = () => {
    const tenant = useCurrentTenant();

    const customTenantLogo = tenant?.hasLogo
        ? `/api/tenants/${tenant.tenantId}/logo`
        : null;

    if (customTenantLogo) {
        return (
            <Link
                to="/"
                className="mb-6 rounded-lg shadow-sm block w-full min-h-24 h-24 py-2 overflow-hidden lg:rounded-lg lg:bg-white lg:shadow-sm lg:ring-1 lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10"
                style={{ lineHeight: 0, minHeight: "6rem" }}>
                <img
                    src={customTenantLogo}
                    className="object-contain w-full h-full"
                    alt="Tenant Logo"
                    style={{ display: "block", minHeight: "4rem" }}
                />
            </Link>
        );
    }

    return null;
};

export const NgSidebar: React.FC = () => {
    const user = useCurrentUser();
    const { pathname } = useLocation();
    const { settings } = useTenantSettings();
    const publicProfileSettingEnabled = settings.enablePublicProfile;

    // const permissions = useSelector((state: ApplicationState) => state.permissions);
    // const region = useSelector((state: ApplicationState) => state.region);

    const reportsMode = useSelector((state: ApplicationState) =>
        isAllowed(ActionType.ViewReports, state.permissions, state.region)
            ? "full-access"
            : "custom-only",
    );

    const showReferrals = useSelector((state: ApplicationState) =>
        isAllowed(ActionType.ViewReference, state.permissions, state.region),
    );
    const isClientsActive =
        pathname === "/" ||
        pathname === "/pipeline" ||
        pathname === "/timeline" ||
        pathname.startsWith("/clients");

    const isFilesActive = pathname.startsWith("/files");
    const isTasksActive = pathname.startsWith("/tasks");
    const isReportsActive = pathname.startsWith("/reports");
    const entitiesWithoutActiveTasksEnabled = useFeature("entitiesWithoutActiveTasks");
    const legacyReportsEnabled = useIsAllowed(ActionType.ViewReports);
    const viewAllEnabled = useIsAllowed(ActionType.ViewAllInvoices);
    const viewOwnEnabled = useIsAllowed(ActionType.ViewOwnInvoices);
    const viewCustomReportsEnabled = useIsAllowed(
        ActionType.AdvancedSearchExplicitExecute,
    );

    return (
        <Sidebar>
            <SidebarBody>
                <NgAppLogo />
                <SidebarSection>
                    <SidebarItem href="/pipeline" current={isClientsActive}>
                        <UsersIcon />
                        <SidebarLabel>Clients</SidebarLabel>
                    </SidebarItem>
                    {isClientsActive ? (
                        <>
                            <SidebarItem
                                href="/pipeline"
                                current={pathname === "/pipeline"}>
                                <SidebarLabel className="ml-6">Pipeline</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                href="/timeline"
                                current={pathname === "/timeline"}>
                                <SidebarLabel className="ml-6">Timeline</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                href="/clients"
                                current={pathname === "/clients"}>
                                <SidebarLabel className="ml-6">All Clients</SidebarLabel>
                            </SidebarItem>
                        </>
                    ) : null}

                    <SidebarItem
                        href="/communities"
                        current={pathname.startsWith("/communities")}>
                        <BuildingOfficeIcon />
                        <SidebarLabel>Communities</SidebarLabel>
                    </SidebarItem>

                    {showReferrals && (
                        <SidebarItem
                            href="/references"
                            current={pathname.startsWith("/references")}>
                            <UserIcon />
                            <SidebarLabel>Referrals</SidebarLabel>
                        </SidebarItem>
                    )}

                    <SidebarItem href="/tasks" current={pathname.startsWith("/tasks")}>
                        <ClipboardDocumentCheckIcon />
                        <SidebarLabel>Tasks</SidebarLabel>
                    </SidebarItem>
                    {isTasksActive ? (
                        <>
                            <SidebarItem href="/tasks" current={pathname === "/tasks"}>
                                <SidebarLabel className="ml-6">Active Tasks</SidebarLabel>
                            </SidebarItem>
                            {entitiesWithoutActiveTasksEnabled && (
                                <SidebarItem
                                    href="/tasks/missing"
                                    current={pathname === "/tasks/missing"}>
                                    <SidebarLabel className="ml-6">
                                        Entities Without Tasks
                                    </SidebarLabel>
                                </SidebarItem>
                            )}
                        </>
                    ) : null}

                    <SidebarItem href="/files" current={pathname.startsWith("/files")}>
                        <FolderIcon />
                        <SidebarLabel>Files</SidebarLabel>
                    </SidebarItem>
                    {isFilesActive ? (
                        <>
                            <SidebarItem href="/files" current={pathname === "/files"}>
                                <SidebarLabel className="ml-6">Our Files</SidebarLabel>
                            </SidebarItem>
                            <SidebarItem
                                href="/files/signwise"
                                current={pathname.startsWith("/files/signwise")}>
                                <SidebarLabel className="ml-6">Signwise</SidebarLabel>
                            </SidebarItem>
                        </>
                    ) : null}

                    <SidebarItem
                        href={
                            reportsMode === "custom-only"
                                ? "/reports/show/custom"
                                : "/reports"
                        }
                        current={pathname.startsWith("/reports")}>
                        <ChartBarIcon />
                        <SidebarLabel>Reports</SidebarLabel>
                    </SidebarItem>
                    {isReportsActive ? (
                        <>
                            <SidebarItem
                                href="/reports"
                                current={pathname === "/reports"}>
                                <SidebarLabel className="ml-6">Overview</SidebarLabel>
                            </SidebarItem>
                            {legacyReportsEnabled &&
                            (viewAllEnabled || viewOwnEnabled) ? (
                                <SidebarItem
                                    href="/reports/show/revenue"
                                    current={pathname.startsWith(
                                        "/reports/show/revenue",
                                    )}>
                                    <SidebarLabel className="ml-6">
                                        Client Placement
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                            {legacyReportsEnabled ? (
                                <SidebarItem
                                    href="/reports/show/referrals"
                                    current={pathname.startsWith(
                                        "/reports/show/referrals",
                                    )}>
                                    <SidebarLabel className="ml-6">
                                        Referrals
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                            {viewCustomReportsEnabled ? (
                                <SidebarItem
                                    href="/reports/show/custom"
                                    current={pathname.startsWith("/reports/show/custom")}>
                                    <SidebarLabel className="ml-6">
                                        Custom Reports
                                    </SidebarLabel>
                                </SidebarItem>
                            ) : null}
                        </>
                    ) : null}
                </SidebarSection>

                <SidebarSpacer />
                <SidebarSection>
                    <WhiteLabelRestricted>
                        <SidebarItem href="https://docs.seniorplace.io/" target="_blank">
                            <QuestionMarkCircleIcon />
                            <SidebarLabel>Help</SidebarLabel>
                        </SidebarItem>
                    </WhiteLabelRestricted>
                    {publicProfileSettingEnabled ? (
                        <SidebarItem
                            href="/advisor-directory"
                            current={pathname.startsWith("/advisor-directory")}>
                            <UserGroupIcon />
                            <SidebarLabel>Advisor Directory</SidebarLabel>
                        </SidebarItem>
                    ) : null}
                    <SidebarItem
                        href="/settings"
                        current={pathname.startsWith("/settings")}>
                        <Cog8ToothIcon />
                        <SidebarLabel>Settings</SidebarLabel>
                    </SidebarItem>
                </SidebarSection>
            </SidebarBody>
            {user ? (
                <SidebarFooter className="max-lg:hidden">
                    <Dropdown>
                        <DropdownButton as={SidebarItem}>
                            <span className="flex min-w-0 items-center gap-3">
                                <Avatar
                                    initials={getInitials(user.name)}
                                    className="size-10"
                                    square
                                    alt=""
                                />
                                <span className="min-w-0">
                                    <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                                        {user.name}
                                    </span>
                                    <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                                        {user.email}
                                    </span>
                                </span>
                            </span>
                            <ChevronUpIcon />
                        </DropdownButton>
                        <DropdownMenu className="min-w-64" anchor={"top start"}>
                            <DropdownItem href="#">
                                <UserCircleIcon />
                                <DropdownLabel>My account</DropdownLabel>
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem href="#">
                                <ShieldCheckIcon />
                                <DropdownLabel>Privacy policy</DropdownLabel>
                            </DropdownItem>
                            <DropdownItem href="#">
                                <LightBulbIcon />
                                <DropdownLabel>Share feedback</DropdownLabel>
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem href="/login">
                                <ArrowRightStartOnRectangleIcon />
                                <DropdownLabel>Sign out</DropdownLabel>
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </SidebarFooter>
            ) : null}
        </Sidebar>
    );
};
